// The Front Page Quiz: a daily recall quiz built entirely on the client from
// today's cards — no server quiz card needed. Der Spiegel's daily news quiz
// and NYT's news+games cohort both show recall quizzes are the strongest
// retention loop a news product can run; this one asks the reader to match a
// story's own key fact back to its headline. Deterministic per day (same
// seeded shuffle for everyone), read-first (stories the reader actually
// opened lead the question order).

import { deepDiveFor } from "../deepDive";
import type { InSnipCard } from "../types";
import { seededRng } from "./localGame";

export type QuizQuestion = { q: string; options: string[]; answer: number; why?: string };

function shortHeadline(headline: string, max = 72): string {
  if (headline.length <= max) return headline;
  const cut = headline.slice(0, max);
  return cut.slice(0, Math.max(30, cut.lastIndexOf(" "))) + "…";
}

/**
 * Build today's quiz as a synthetic quiz card the existing QuizModal renders
 * as-is. Returns null when today's edition has too little grounded material
 * (fewer than 3 stories with usable key facts).
 */
export function buildFrontPageQuiz(
  cards: InSnipCard[],
  readIds: ReadonlySet<string>,
  day: string,
): InSnipCard | null {
  const random = seededRng("fpq-" + day);

  // One fact per story. Read stories lead: recall is the point.
  const pool: { card: InSnipCard; label: string; value: string }[] = [];
  const ordered = [...cards].sort((a, b) => Number(readIds.has(b.id)) - Number(readIds.has(a.id)));
  for (const card of ordered) {
    const fact = (deepDiveFor(card)?.keyFacts ?? []).find(
      (f) => f.label && f.value && !card.headline.includes(f.value),
    );
    if (fact) pool.push({ card, label: fact.label, value: fact.value });
  }
  if (pool.length < 3) return null;

  const questions: QuizQuestion[] = pool.slice(0, 5).map(({ card, label, value }) => {
    // Three distractor headlines from the rest of today's edition.
    const others = cards.filter((c) => c.id !== card.id);
    for (let i = others.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [others[i], others[j]] = [others[j], others[i]];
    }
    const options = [card, ...others.slice(0, 3)].map((c) => shortHeadline(c.headline));
    // Seeded position for the right answer.
    const answer = Math.floor(random() * options.length);
    [options[0], options[answer]] = [options[answer], options[0]];
    return {
      q: `Which story is this from? "${label}: ${value}"`,
      options,
      answer,
      why: card.essence ? String(card.essence).slice(0, 160) : undefined,
    };
  });

  return {
    id: `quiz-local-${day}`,
    headline: "The Front Page Quiz",
    essence: "Five facts from today's edition. Match each one to its story.",
    body: "",
    category: "Culture & Curiosity",
    tags: [],
    primarySourceName: "InSnip",
    primarySourceUrl: "https://insnip.com",
    supportingSources: [],
    payload: { questions },
  } as unknown as InSnipCard;
}
